import {
  SummaryData
  // SummaryTitleColor
} from 'components/SummaryDetail/SummaryDetail';
import { compact } from 'lodash';
import i18n from 'i18n';
import { BeaconMessageFormType } from 'core/beaconMessage/BeaconMessage';
import { getDayPartSummary } from 'components/Dayparts/Dayparts';
import { LineMessageType } from 'core/advertiser/LineBeacon';
import {
  renderCouponMessage,
  renderImageMessage,
  renderJoinMessage,
  renderVideoMessage
} from 'containers/Creatives/CreativeSetupFlow/FlowSteps/LineMessageRenderMethod';
import {
  FlowSummarySectionProps,
  SummarySectionData
} from 'components/FlowSummarySection/FlowSummarySection';
// import { DefaultCreativeManager } from 'core/creative/CreativeManager';
// import { LimitationData } from 'core/limitation/Limitation';

function getFrequencyCapValue(
  intervalHours: number,
  maxFrequency: number
): string | undefined {
  if (intervalHours <= 0 || maxFrequency <= 0) {
    return undefined;
  }
  const timeUnitKey = +intervalHours > 1 ? 'hours' : 'hour';
  const cappingCountKey = +maxFrequency > 1 ? 'cappingCounts' : 'cappingCount';
  return `${
    i18n.t('beaconFields.labels.frequencyCap.every') || ''
  } ${intervalHours} ${i18n.t(`common.units.${timeUnitKey}`)} ${i18n.t(
    'beaconFields.labels.frequencyCap.send'
  )} ${maxFrequency} ${i18n.t(
    `beaconFields.labels.frequencyCap.${cappingCountKey}`
  )}`;
}

function getLineMessageSummary(
  beaconMessage: BeaconMessageFormType
): SummaryData[] {
  const lineMessage = beaconMessage.creative.lineMessage;
  if (!lineMessage || lineMessage.length === 0) {
    return [];
  }
  return compact(
    lineMessage.map((message, index) => {
      let value: string | JSX.Element | undefined;
      if (message.type === LineMessageType.TEXT) {
        value = message.message;
      }
      if (message.type === LineMessageType.IMAGE) {
        value = renderImageMessage(message.image);
      }
      if (message.type === LineMessageType.VIDEO) {
        value = renderVideoMessage(message.video, message.previewImage);
      }
      if (message.type === LineMessageType.JOIN) {
        value = renderJoinMessage(message.officialAccount);
      }
      if (message.type === LineMessageType.COUPON) {
        value = renderCouponMessage(message.couponId);
      }
      if (!value) return undefined;
      return {
        label: `${i18n.t('creativeSummaryStep.labels.lineMessage')} ${index + 1}`,
        value
      };
    })
  );
}

function getDayPartValue(
  beaconMessage: BeaconMessageFormType
): string | undefined {
  const dayPart = beaconMessage.dayPart;
  if (!dayPart || !dayPart.enabled) {
    return undefined;
  }
  return getDayPartSummary(dayPart);
}

// function getLimitationSummary(beaconMessage: BeaconMessageFormType): {
//   [key: string]: SummaryData[];
// } {
//   const limitations = beaconMessage.limitations;
//   if (!limitations) return {};
//   const creativeManager = new DefaultCreativeManager();
//   return creativeManager.getLimitationSummaryData(limitations);
// }

export type BeaconMessageSummarySections = {
  basicSummaryData: FlowSummarySectionProps;
  creativeSummaryData: FlowSummarySectionProps;
  // deviceSummaryData: FlowSummarySectionProps | undefined;
};

function getBasicSummaryData(
  beaconMessage: BeaconMessageFormType
): SummarySectionData {
  const dayPartValue = getDayPartValue(beaconMessage);
  return {
    basic: {
      content: compact([
        {
          label: i18n.t('beaconMessageForm.labels.name'),
          value: beaconMessage.name
        },
        {
          label: i18n.t('beaconMessageForm.labels.schedule'),
          value: `${beaconMessage.startDate} ~ ${beaconMessage.endDate}`
        },
        dayPartValue
          ? {
              label: i18n.t('campaignSummary.labels.dayPart'),
              value: dayPartValue,
              showArea: true
            }
          : undefined
      ])
    }
  };
}

function getCreativeSummaryData(
  beaconMessage: BeaconMessageFormType
): SummarySectionData {
  const { intervalHours, maxFrequency } = beaconMessage.creative.typeProperties;
  const frequencyCapValue = getFrequencyCapValue(intervalHours, maxFrequency);
  return {
    creative: {
      content: compact([
        beaconMessage.creative.typeProperties.beaconConnection
          ? {
              label: i18n.t('beaconFields.labels.beaconConnection'),
              value:
                beaconMessage.creative.typeProperties.beaconConnection.label
            }
          : undefined,
        beaconMessage.creative.typeProperties.stores.length > 0
          ? {
              label: i18n.t('beaconMessageFields.labels.deviceSetting'),
              value: beaconMessage.creative.typeProperties.stores
                .map((store) => i18n.t(`store.${store}`))
                .join(', ')
            }
          : undefined,
        {
          label: i18n.t('beaconFields.labels.frequencyCapSwitch'),
          value: frequencyCapValue
            ? frequencyCapValue
            : i18n.t('summary.titles.noData')
        },
        ...getLineMessageSummary(beaconMessage)
      ])
    }
  };
}

// function getDeviceSummaryData(
//   beaconMessage: BeaconMessageFormType
// ): SummarySectionData | undefined {
//   const limitationData = getLimitationSummary(beaconMessage);
//   if (Object.keys(limitationData).length === 0) return undefined;
//   return {
//     include: {
//       title: i18n.t('campaignSummary.titles.inc'),
//       titlePrefixColor: SummaryTitleColor.GREEN,
//       content: limitationData.include
//     }
//   };
// }

export function getBeaconMessageSummarySections(
  beaconMessage: BeaconMessageFormType | undefined
): BeaconMessageSummarySections | undefined {
  if (!beaconMessage) return undefined;

  // const deviceSummaryData = getDeviceSummaryData(beaconMessage);
  return {
    basicSummaryData: {
      title: i18n.t('beaconMessageSetupFlowPage.titles.basicSection'),
      summaryData: getBasicSummaryData(beaconMessage)
    },
    creativeSummaryData: {
      title: i18n.t('beaconMessageSetupFlowPage.titles.creativeSection'),
      summaryData: getCreativeSummaryData(beaconMessage)
    }
    // deviceSummaryData: deviceSummaryData
    //   ? {
    //       title: i18n.t(
    //         'beaconMessageSetupFlowPage.titles.deviceSetupSection'
    //       ),
    //       summaryData: deviceSummaryData
    //     }
    //   : undefined
  };
}
